import { environment } from "../../../environments/environment";

/**
 * Configuração, por ambiente, da migração incremental do front velho (este projeto) para o
 * front novo (prosper-web). É uma **feature flag de build-time**: `environment.frontNovo`
 * decide, por ambiente, quais módulos já pertencem ao front novo.
 *
 * Núcleo **puro** (sem Angular/DI) compartilhado pelo guard de rota
 * ([[FrontNovoRedirectGuard]]) e pelo seletor de menu (`pickFrontNovoMenu`), garantindo uma
 * única fonte de verdade entre "para onde a rota redireciona" e "qual forma o menu mostra".
 */
export interface FrontNovoConfig {
    /**
     * Base do front novo. **Opcional — default `""` (mesma origem).** No cenário normal
     * (Front Door na frente, mesmo domínio) o edge roteia o path, então nem precisa ser
     * definido. Só sobrescreva com URL absoluta onde os fronts estão em **origens diferentes**
     * e não há Front Door — na prática, apenas o **dev local** (front novo em `localhost:5173`).
     */
    baseUrl?: string;
    /** Chaves de [[FRONT_NOVO_MODULES]] já migradas neste ambiente. Vazio = tudo legado. */
    migratedModules: string[];
}

/**
 * Regra de mapeamento de um path do front velho para o destino no front novo. `to` omitido =
 * **preserva** o path de origem (identidade, mantendo query/fragment). Com `to`, **reescreve**
 * para um destino fixo.
 */
export interface FrontNovoRouteRule {
    /** Path de origem no front velho (ex.: `/clientes/cadastro`). */
    from: string;
    /** `exact` (default) casa o path inteiro; `prefix` casa `from` e qualquer subpath dele. */
    match?: "exact" | "prefix";
    /** Destino no front novo. Omitido = preserva `from` (path+query). */
    to?: string;
}

/** Configuração de um módulo migrável: como casar e para onde redirecionar suas rotas. */
export interface FrontNovoModuleConfig {
    /** Prefixo de rota que identifica o módulo (ex.: `/clientes`). Usado pelo guard. */
    prefix: string;
    /** Destino no front novo para qualquer path do módulo sem regra que case. */
    defaultTarget: string;
    /** Regras old→new avaliadas em ordem; a **primeira que casar vence**. */
    rules?: FrontNovoRouteRule[];
}

/**
 * Registro dos módulos migráveis. Declarar um módulo aqui é o passo 1 para migrá-lo; depois
 * basta ligá-lo por ambiente em `frontNovo.migratedModules`. As `rules` definem o mapeamento
 * old→new (preservar / reescrever), com fallback em `defaultTarget`.
 */
export const FRONT_NOVO_MODULES: Record<string, FrontNovoModuleConfig> = {
    clientes: {
        prefix: "/clientes",
        defaultTarget: "/clientes",
        rules: [
            { from: "/clientes/cadastro" }, // exato, sem `to` → preserva (front novo tem a tela)
            // Escala — reescrever para outro path no front novo:
            //   { from: "/clientes/algo", to: "/novo/destino" },
            // Preservar uma subárvore inteira:
            //   { from: "/clientes/x", match: "prefix" },
        ],
    },
};

/** Lê a config do ambiente com fallback seguro (nada migrado). */
function getConfig(): FrontNovoConfig {
    return environment.frontNovo ?? { baseUrl: "", migratedModules: [] };
}

/** `true` se o módulo (`FRONT_NOVO_MODULES` key) está migrado no ambiente atual. */
export function isModuleMigrated(key: string): boolean {
    return getConfig().migratedModules.includes(key);
}

/** Extrai o path puro de um `url` (sem query string / fragment). */
function pathOf(url: string): string {
    return url.split("?")[0].split("#")[0];
}

/** Acha o módulo **migrado** dono do `url` (por prefixo), ou `null`. */
function findMigratedModule(url: string): { key: string; mod: FrontNovoModuleConfig } | null {
    const path = pathOf(url);
    for (const [key, mod] of Object.entries(FRONT_NOVO_MODULES)) {
        if ((path === mod.prefix || path.startsWith(`${mod.prefix}/`)) && isModuleMigrated(key)) {
            return { key, mod };
        }
    }
    return null;
}

/**
 * Dado o `url` de uma navegação, retorna a key do módulo migrado dono daquele path, ou `null`.
 * Usado pelo guard para decidir **se** redireciona.
 *
 * @param url URL da rota (ex.: `/clientes/saldo?foo=1`).
 */
export function resolveMigratedModuleForUrl(url: string): string | null {
    return findMigratedModule(url)?.key ?? null;
}

/**
 * Mapeia o `url` do front velho para o **path de destino no front novo**, aplicando as `rules`
 * do módulo (1ª que casar vence): `to` definido → reescreve; `to` omitido → preserva o `url`
 * (path+query); sem regra → `defaultTarget`. Se o `url` não pertence a módulo migrado, devolve
 * o próprio `url` (fallback seguro; o guard só chama isto quando há módulo migrado).
 *
 * @param url URL da rota atual (ex.: `/clientes/cadastro/1`).
 */
export function mapToNewFrontendPath(url: string): string {
    const found = findMigratedModule(url);
    if (!found) return url;
    const path = pathOf(url);
    for (const rule of found.mod.rules ?? []) {
        const hit =
            rule.match === "prefix" ? path === rule.from || path.startsWith(`${rule.from}/`) : path === rule.from;
        if (hit) {
            return rule.to ?? url; // to omitido → preserva path+query
        }
    }
    return found.mod.defaultTarget;
}

/**
 * Prefixa o path de destino com a base do ambiente. Base vazia → path relativo (mesma origem,
 * Front Door roteia). Normaliza a barra entre base e path. O **mapeamento** old→new é feito por
 * [[mapToNewFrontendPath]]; aqui só se resolve a origem.
 *
 * @param path Path de destino no front novo (ex.: `/clientes`).
 */
export function buildNewFrontendUrl(path: string): string {
    const base = (getConfig().baseUrl ?? "").replace(/\/+$/, "");
    const normalizedPath = `/${path.replace(/^\/+/, "")}`;
    return `${base}${normalizedPath}`;
}
